import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { Message } from '../../pages/cardex/api/useMessage';

import { canalDe, ACTION_TYPE_EMAIL } from '../config/canaux';
import { heureBulle, texteMessage } from '../helpers/conversation';
import { t } from '../i18n/langue';

import PiecesJointes, { aDesPiecesJointes } from './PiecesJointes';
import Text from './Text';

/**
 * Une bulle du fil — #16, variante A.
 *
 * **À plat, sans repli.** Pas de séparateur dépliable pour les automatismes : la
 * variante B de #16 reste écartée, cette fois sur son repli autant que sur sa
 * liste (#31 §2). Ce qui est affiché est affiché ; ce qui est masqué l'est par la
 * bascule, et le fil le dit en permanence.
 *
 * **Toute bulle est tronquée à six lignes.** L'e-mail s'ouvre en plein écran à
 * la demande : jamais de HTML rendu intégralement en ligne (variante C, écartée :
 * un e-mail de 400 lignes noierait le fil, et le rendu de HTML arbitraire dans un
 * flux vertical ne se contrôle pas). Les autres canaux (WhatsApp, SMS, bot, live
 * chat) n'ont pas de plein écran : leur texte se déplie en place par « Voir
 * plus », qui n'apparaît que si le texte déborde réellement (retour Odomo,
 * 2026-09-29 : un long WhatsApp était illisible au-delà de six lignes).
 *
 * Les bulles envoyées sont en couleur de marque (contraste AA, #34) ; le canal est
 * porté par l'icône de la ligne d'heure.
 */
interface BulleMessageProps {
    message: Message;
    onOuvrirEmail: (message: Message) => void;
}

/** Six lignes : la troncature décidée par #16, exprimée en classe utilitaire. */
const SIX_LIGNES = 'line-clamp-6 whitespace-pre-wrap';

/** Déplié : les retours à la ligne du message restent, la troncature saute. */
const SANS_TRONCATURE = 'whitespace-pre-wrap';

/*
 * `memo` (audit 2026-09-23) : le fil se recharge toutes les 30 s, et le partage
 * structurel de React Query rend le MÊME objet pour un message inchangé. Sans
 * `memo`, chaque rechargement re-rendait toutes les bulles du fil.
 */
function BulleMessage({ message, onOuvrirEmail }: BulleMessageProps) {
    const recu = message.is_received;
    const canal = canalDe(message.action_type_id);
    const estEmail = message.action_type_id === ACTION_TYPE_EMAIL;

    // Le corps HTML n'est jamais rendu ici : on montre son TEXTE, et le HTML part
    // en plein écran. Un e-mail n'a rien dans `message` — tout est dans `body` —
    // d'où la conversion : sans elle la bulle n'affichait qu'un tiret.
    // Mémoïsé par message : pour un e-mail, c'est une analyse DOMParser du HTML
    // complet, à ne pas refaire à chaque rendu (audit 2026-09-23).
    const texte = useMemo(
        () => texteMessage({ message: message.message, body: message.body }),
        [message.message, message.body],
    );

    // « Voir plus » (retour Odomo) : hors e-mail, qui a son plein écran.
    const repliable = !estEmail;
    const refTexte = useRef<HTMLElement>(null);
    const [deborde, setDeborde] = useState(false);
    const [deplie, setDeplie] = useState(false);

    // Mesurer AVANT la peinture : sinon le bouton apparaît une image plus tard et
    // le fil saute. On ne mesure que REPLIÉ : déplié, le texte ne déborde plus par
    // définition, et re-mesurer ferait disparaître « Voir moins ». Relancé quand
    // le texte change (rechargement du fil) et au repli.
    useLayoutEffect(() => {
        if (!repliable || deplie) return;
        const el = refTexte.current;
        setDeborde(el ? el.scrollHeight > el.clientHeight : false);
    }, [texte, deplie, repliable]);

    return (
        <div className={`mb-2 flex ${recu ? 'justify-start' : 'justify-end'}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                    recu
                        ? 'rounded-bl-md bg-white text-text-color'
                        : 'rounded-br-md bg-main-color text-white'
                }`}
            >
                {estEmail && message.object && (
                    <Text
                        as="p"
                        variante="petit"
                        className={`mb-1 font-semibold ${recu ? '' : 'text-white'}`}
                    >
                        {message.object}
                    </Text>
                )}

                <PiecesJointes message={message} sortant={!recu} />

                {(texte || !aDesPiecesJointes(message)) && (
                    <Text
                        ref={refTexte}
                        as="p"
                        variante="petit"
                        className={`${deplie ? SANS_TRONCATURE : SIX_LIGNES} ${recu ? '' : 'text-white'}`}
                    >
                        {texte || '—'}
                    </Text>
                )}

                {repliable && deborde && (
                    // mobile-check-ignore — mêmes classes et même raison que « Ouvrir l’e-mail » :
                    // min-h-touch posée ici, sans la pleine largeur du socle ; blanc sur bulle envoyée.
                    <button
                        type="button"
                        aria-expanded={deplie}
                        onClick={() => setDeplie((d) => !d)}
                        className={`-mx-1 mt-1 inline-flex min-h-touch items-center px-1 font-semibold underline ${recu ? 'text-main-color' : 'text-white'}`}
                    >
                        {deplie ? t('messagerie.bulle.voirMoins') : t('messagerie.bulle.voirPlus')}
                    </button>
                )}

                {estEmail && message.body && (
                    // mobile-check-ignore — contrôle propre à la bulle ; la hauteur tactile
                    // (min-h-touch) est posée ici, sans la pleine largeur du socle.
                    <button
                        type="button"
                        onClick={() => onOuvrirEmail(message)}
                        className={`-mx-1 mt-1 inline-flex min-h-touch items-center px-1 font-semibold underline ${recu ? 'text-main-color' : 'text-white'}`}
                    >
                        {t('messagerie.bulle.ouvrirEmail')}
                    </button>
                )}

                <Text as="p" variante="petit" className={`mt-1 flex items-center justify-end gap-1 ${recu ? 'text-text-color-light' : 'text-white'}`}>
                    <canal.Icone aria-hidden="true" className="h-3.5 w-3.5" />
                    <span className="sr-only">{canal.libelle}, </span>
                    {heureBulle(message.created_at)}
                </Text>
            </div>
        </div>
    );
}

export default memo(BulleMessage);
